import type { SourceCard } from '../services/api'
import { allowedDeepLink } from '../components/chat/sourceCardPolicy'

/**
 * The Ask hand-off card (v6 prototype: the dock's `m.hasHandoff` card and `runHandoff`; MAR-317).
 *
 * In v6 an answer can end in a gold card — a line saying where the rest of it lives and
 * one button that takes the merchant there ("Show the skips in the Ledger", "Open Value").
 *
 * ⚠️ **Built by code, from the answer's own source cards — never from model text.** v6's
 * hand-offs are canned per answer, and some do more than the product can: "Show the
 * skips" FILTERS the Ledger, and "Open her record" opens a record. Neither exists here.
 *
 * ⚠️ **Keyed by CAPABILITY, not by link (PR #321 review).** A card's `deepLink` is the
 * capability's home surface (`registry.js` `surfaceLink`), which is not always a surface that
 * SHOWS what was read: `read_sales_report`, `read_value` and `find_orders` link to the Ledger,
 * which holds decisions, not sales, value or orders; `read_product_report` and
 * `read_customer_mix` link to Knowledge, which holds beliefs about customers and products,
 * not reports. Keyed by link, the card told the merchant "what I read for this is in the
 * Ledger" after a sales read — sending her to look for something that is not there. So a
 * capability earns a card only when its surface shows that data, and its copy names only
 * that. A capability with no entry here — those five, the two `prepare_*` proposals, and
 * anything added later — earns no card until someone decides where its data lives.
 *
 * The card's own link must still be the allowlisted one this table expects (`sourceCardPolicy`,
 * mirroring `deep-links.js`): a card whose link disagrees with its capability is data that
 * crossed a network, and earns nothing.
 *
 * Only a read that happened (`ok` / `partial`) earns a hand-off — "the rest is in the
 * Ledger" after a read that failed would send the merchant to look for something nobody
 * saw. Earlier reads (`priorSources`) never do: they are not this answer's.
 */
export interface AskHandoff {
  to: string
  /** The button. */
  label: string
  /** The line beside it. */
  note: string
}

/** Copy per capability whose home surface shows what it read. No entry, no card. */
export const HANDOFF_BY_CAPABILITY: Readonly<Record<string, AskHandoff>> = Object.freeze({
  explain_decision: { to: '/ledger', label: 'Show it in the Ledger', note: 'The decision I read is in the Ledger.' },
  list_attention_items: { to: '/ledger?inbox=open', label: 'Open the Inbox', note: 'What is waiting on you is in the Inbox.' },
  read_customer_memory: { to: '/knowledge', label: 'Open Knowledge', note: 'What I know about your customers is in Knowledge.' },
  find_customers: { to: '/knowledge', label: 'Open Knowledge', note: 'What I know about your customers is in Knowledge.' },
  read_product_knowledge: { to: '/knowledge', label: 'Open Knowledge', note: 'What I know about your products is in Knowledge.' },
  find_products: { to: '/knowledge', label: 'Open Knowledge', note: 'What I know about your products is in Knowledge.' },
  read_authority: { to: '/authority?tab=authority', label: 'Open Authority', note: 'What I may do is set on Authority.' },
})

export function handoffFor(sources: readonly SourceCard[] | undefined | null): AskHandoff | null {
  if (!sources) return null
  for (const card of sources) {
    if (card?.status !== 'ok' && card?.status !== 'partial') continue
    const capability = card.capability
    if (typeof capability !== 'string' || !Object.prototype.hasOwnProperty.call(HANDOFF_BY_CAPABILITY, capability)) continue
    const handoff = HANDOFF_BY_CAPABILITY[capability]
    if (allowedDeepLink(card.deepLink)?.to !== handoff.to) continue
    return { ...handoff }
  }
  return null
}
